import React, { useEffect, useState } from 'react';
import { adminAnalyticsAPI } from '../api';
import { fmtDate } from '../utils/date';

// "3h ago", "5d ago", "just now" — compact relative time for last-seen columns.
function relTime(val) {
  if (!val) return 'never';
  const then = new Date(val).getTime();
  if (isNaN(then)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60)        return 'just now';
  const m = Math.floor(s / 60);   if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);   if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);   if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

// Minutes → "45m", "2h 10m", "3h".
function fmtMins(min) {
  const n = Math.round(Number(min) || 0);
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60), m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Days since last activity → colour (fresh = green, stale = amber, dormant = red).
function activityColor(lastSeen) {
  if (!lastSeen) return '#ef4444';
  const days = (Date.now() - new Date(lastSeen).getTime()) / 86400000;
  if (days <= 7)  return '#22c55e';
  if (days <= 30) return '#f59e0b';
  return '#ef4444';
}

const WINDOWS = [7, 30, 90];

function Stat({ label, value, sub }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '0.9rem 1.1rem', minWidth: 140 }}>
      <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e2e8f0', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const SORTS = {
  recent:   { label: 'Most recently active', fn: (a, b) => new Date(b.last_seen_at || 0) - new Date(a.last_seen_at || 0) },
  time:     { label: 'Most time on app',     fn: (a, b) => Number(b.active_min_window) - Number(a.active_min_window) },
  logins:   { label: 'Most logins',          fn: (a, b) => Number(b.logins_window) - Number(a.logins_window) },
  dormant:  { label: 'Most dormant',         fn: (a, b) => new Date(a.last_seen_at || 0) - new Date(b.last_seen_at || 0) },
};

export default function Activity() {
  const [overview, setOverview] = useState(null);
  const [users,    setUsers]    = useState([]);
  const [win,      setWin]      = useState(30);
  const [sort,     setSort]     = useState('recent');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([adminAnalyticsAPI.overview(), adminAnalyticsAPI.users(win)])
      .then(([o, u]) => { setOverview(o.data.overview); setUsers(u.data.users || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [win]);

  const sorted = [...users].sort(SORTS[sort].fn);

  const th = { textAlign: 'left', padding: '0.5rem 0.6rem', fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' };
  const td = { padding: '0.5rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #1e293b', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem' }}>
        <h1 style={{ margin: 0 }}>User Activity</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          {WINDOWS.map(w => (
            <button key={w} onClick={() => setWin(w)}
              style={{ padding: '0.35rem 0.8rem', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer',
                       border: '1px solid #334155', background: win === w ? '#8b5cf6' : 'transparent', color: win === w ? '#fff' : '#cbd5e1' }}>
              {w}d
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 4 }}>
        Engagement over the last {win} days. Time-on-app is measured from in-app activity; it accrues from the day this feature ships.
      </p>

      {loading && <p>Loading…</p>}

      {overview && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', margin: '1rem 0 1.5rem' }}>
          <Stat label="Total users"   value={overview.total_users} sub={`${overview.new_30d} new in 30d`} />
          <Stat label="Active today"  value={overview.active_1d} />
          <Stat label="Active 7d"     value={overview.active_7d} />
          <Stat label="Active 30d"    value={overview.active_30d} />
          <Stat label="Dormant 30d+"  value={overview.dormant_30d} sub={`${overview.never_logged_in} never logged in`} />
          <Stat label="Logins 7d"     value={overview.logins_7d} sub={`${overview.logins_30d} in 30d`} />
          <Stat label="Avg session"   value={fmtMins(overview.avg_session_min_30d)} sub="last 30d" />
          <Stat label="Total time 30d" value={fmtMins(overview.total_active_min_30d)} sub={`${overview.sessions_30d} sessions`} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Sort:</span>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ padding: '0.3rem 0.5rem', borderRadius: 6, background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '0.8rem' }}>
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{sorted.length} users</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>User</th>
              <th style={th}>Company / Business</th>
              <th style={th}>Plan</th>
              <th style={th}>Last seen</th>
              <th style={th}>Last login</th>
              <th style={th}>Logins ({win}d)</th>
              <th style={th}>Sessions</th>
              <th style={th}>Time on app</th>
              <th style={th}>Sales</th>
              <th style={th}>Joined</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(u => (
              <tr key={u.id}>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{u.name}</div>
                  <div style={{ color: '#64748b', fontSize: '0.72rem' }}>{u.email}</div>
                </td>
                <td style={td}>
                  <div>{u.company_name || '—'}</div>
                  <div style={{ color: '#64748b', fontSize: '0.72rem' }}>{u.business_name || '—'} · {u.role}</div>
                </td>
                <td style={td}>{u.plan || '—'}</td>
                <td style={{ ...td, color: activityColor(u.last_seen_at), fontWeight: 600 }}>{relTime(u.last_seen_at)}</td>
                <td style={td}>{u.last_login_at ? fmtDate(u.last_login_at) : <span style={{ color: '#ef4444' }}>never</span>}</td>
                <td style={{ ...td, textAlign: 'center' }}>{u.logins_window}</td>
                <td style={{ ...td, textAlign: 'center' }}>{u.sessions_window}</td>
                <td style={td}>{fmtMins(u.active_min_window)}</td>
                <td style={{ ...td, textAlign: 'center' }}>{u.sales_window}</td>
                <td style={td}>{fmtDate(u.created_at)}</td>
                <td style={td}>{u.deactivated_at
                  ? <span style={{ color: '#ef4444' }}>Inactive</span>
                  : <span style={{ color: '#22c55e' }}>Active</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
